import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const SendNotification = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    channels: [],
    receiver_type: 'all',
    users: [],
    message: '',
    sendImage: false,
    image: null
  });
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await apiService.get('users');
      if (response.data.status) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleChannelChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      channels: checked 
        ? [...prev.channels, value]
        : prev.channels.filter(ch => ch !== value)
    }));
  };

  const handleReceiverTypeChange = (e) => {
    setFormData(prev => ({
      ...prev,
      receiver_type: e.target.value,
      users: []
    }));
  };

  const handleUserChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      users: selectedOptions
    }));
  };

  const handleMessageChange = (e) => {
    setFormData(prev => ({
      ...prev,
      message: e.target.value
    }));
  };

  const handleSendImageChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      sendImage: checked,
      image: checked ? prev.image : null
    }));
    if (!checked) {
      setImagePreview('');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      
      formData.channels.forEach(channel => {
        submitData.append('channels[]', channel);
      });
      
      submitData.append('receiver_type', formData.receiver_type);
      
      if (formData.receiver_type === 'specific') {
        formData.users.forEach(user => {
          submitData.append('users[]', user);
        });
      }
      
      submitData.append('message', formData.message);
      
      if (formData.sendImage && formData.image) {
        submitData.append('image', formData.image);
      }

      const response = await apiService.postFormData('notifications', submitData);
      
      if (response.data.status === 200) {
        toast.success(response.data.message || 'Notification sent successfully!');
        setFormData({
          channels: [],
          receiver_type: 'all',
          users: [],
          message: '',
          sendImage: false,
          image: null
        });
        setImagePreview('');
      } else {
        toast.error(response.data.message || 'Failed to send notification.');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Something went wrong, try again!!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-0">
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">📢 Send Message</h3>
            <p className="text-sm text-gray-500 mt-1">Choose channels, recipients, and customize your message</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6">
            {/* Channels Section */}
            <div className="mb-7">
              <h5 className="fw-bold mb-4 text-gray-800">
                <i className="bi bi-broadcast text-primary me-2"></i>Select Channels
              </h5>
              <div className="d-flex flex-wrap gap-4">
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="notification"
                    id="ch_notification"
                    checked={formData.channels.includes('notification')}
                    onChange={handleChannelChange}
                  />
                  <label className="form-check-label fw-semibold text-gray-700" htmlFor="ch_notification">
                    🔔 Notification
                  </label>
                </div>
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="whatsapp"
                    id="ch_whatsapp"
                    checked={formData.channels.includes('whatsapp')}
                    onChange={handleChannelChange}
                  />
                  <label className="form-check-label fw-semibold text-gray-700" htmlFor="ch_whatsapp">
                    💬 WhatsApp
                  </label>
                </div>
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value="telegram"
                    id="ch_telegram"
                    checked={formData.channels.includes('telegram')}
                    onChange={handleChannelChange}
                  />
                  <label className="form-check-label fw-semibold text-gray-700" htmlFor="ch_telegram">
                    📨 Telegram
                  </label>
                </div>
              </div>
            </div>

            <div className="separator separator-dashed my-7"></div>

            {/* Receiver Type Section */}
            <div className="mb-7">
              <label className="required form-label fw-bold text-gray-800 fs-6 mb-4">
                <i className="bi bi-people text-primary me-2"></i>Send To
              </label>
              <div className="d-flex flex-wrap gap-4">
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="receiver_type"
                    id="allUsers"
                    value="all"
                    checked={formData.receiver_type === 'all'}
                    onChange={handleReceiverTypeChange}
                  />
                  <label className="form-check-label fw-semibold text-gray-700" htmlFor="allUsers">
                    👥 All Users
                  </label>
                </div>
                <div className="form-check form-check-custom form-check-solid">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="receiver_type"
                    id="specificUsers"
                    value="specific"
                    checked={formData.receiver_type === 'specific'}
                    onChange={handleReceiverTypeChange}
                  />
                  <label className="form-check-label fw-semibold text-gray-700" htmlFor="specificUsers">
                    👤 Specific Users
                  </label>
                </div>
              </div>
            </div>

            {/* User Dropdown */}
            {formData.receiver_type === 'specific' && (
              <div className="mb-7">
                <label className="required form-label fw-bold text-gray-800 fs-6 mb-2">Select Users</label>
                <select
                  name="users[]"
                  className="form-select form-select-solid"
                  multiple
                  value={formData.users}
                  onChange={handleUserChange}
                  style={{ minHeight: '150px' }}
                >
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
                <div className="form-text text-muted">Hold Ctrl/Cmd to select multiple users</div>
              </div>
            )}

            <div className="separator separator-dashed my-7"></div>

            {/* Message Section */}
            <div className="mb-7">
              <h5 className="fw-bold mb-4 text-gray-800">
                <i className="bi bi-chat-text text-primary me-2"></i>Message
              </h5>
              <textarea
                name="message"
                className="form-control form-control-solid"
                rows="6"
                placeholder="Write your message here..."
                value={formData.message}
                onChange={handleMessageChange}
                required
              />
            </div>

            <div className="separator separator-dashed my-7"></div>

            {/* Optional Image Section */}
            <div className="mb-7">
              <h5 className="fw-bold mb-4 text-gray-800">
                <i className="bi bi-image text-primary me-2"></i>Attach Image (Optional)
              </h5>
              <div className="form-check form-check-custom form-check-solid mb-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sendImage"
                  checked={formData.sendImage}
                  onChange={handleSendImageChange}
                />
                <label className="form-check-label fw-semibold text-gray-700" htmlFor="sendImage">
                  Include Image
                </label>
              </div>
              {formData.sendImage && (
                <div>
                  <input
                    type="file"
                    name="image"
                    className="form-control form-control-solid mb-4"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <div className="border border-gray-300 rounded p-3 bg-light">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="d-flex justify-content-end pt-5">
              <button
                type="submit"
                className="btn btn-lg btn-primary"
                disabled={loading}
                style={{ backgroundColor: '#009ef7', borderColor: '#009ef7', minWidth: '200px' }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Please wait...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    🚀 Send Message
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendNotification;
